import {Injectable} from '@angular/core';
import * as dayjs from 'dayjs';
import 'dayjs/locale/sv';
import {BehaviorSubject, Observable} from 'rxjs';
import {HsaObject} from '../types/HsaObject';
import {OpenHour} from '../types/OpenHour';
import {WeekDayIndexesEnum, WeekDaysEnum} from '../enums/CalendarEnums';
import {MetaTag} from '../types/MetaTag';
import {MetaTagFieldsEnum, MetaTypesEnum} from '../enums/SeoEnums';
import {RoutesAddonsEnums} from '../enums/RoutesEnums';
import {ToolsService} from './tools.service';
import {CareService} from '../types/CareService';
import {TitleBuilderService} from './title-builder.service';
import {UrlBuilderService} from './url-builder.service';
import {CareproviderUnitItem} from '../types/CareproviderUnitItem';
import {AppConfigService} from './app-config.service';
import {SeoMetaService} from './seo-meta.service';
import {LinkService} from './link.service';

@Injectable()
export class CareproviderHelper {
  private showOnMapWriteable: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public readonly showOnMap: Observable<boolean> = this.showOnMapWriteable.asObservable();

  private openReviewsWriteable: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public readonly openReviews: Observable<boolean> = this.openReviewsWriteable.asObservable();

  private openCustomerReviewsWriteable: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public readonly openCustomerReviews: Observable<boolean> = this.openCustomerReviewsWriteable.asObservable();

  constructor(
    private tools: ToolsService,
    private titleBuilder: TitleBuilderService,
    private urlBuilder: UrlBuilderService,
    private appConfigService: AppConfigService,
    private metaService: SeoMetaService,
    private linkService: LinkService,
  ) {
    dayjs.locale('sv');
  }

  getOpenningDays(openHour: OpenHour): string {
    let daysString = WeekDaysEnum[WeekDayIndexesEnum[openHour.fromDay]];
    if (openHour.fromDay !== openHour.toDay) {
      daysString += ` - ${WeekDaysEnum[WeekDayIndexesEnum[openHour.toDay]]}`;
    }
    return daysString;
  }

  getOpenningHours(openHour: OpenHour): string {
    return `${this.formatHoursToDisplay(openHour.fromTime)} - ${this.formatHoursToDisplay(openHour.toTime)}`;
  }

  initShowOnMap(): void {
    this.showOnMapWriteable.next(true);
  }

  initOpenReviews(): void {
    this.openReviewsWriteable.next(true);
  }

  initOpenCustomerReviews(): void {
    this.openCustomerReviewsWriteable.next(true);
  }

  formatDate(date: string): string {
    return dayjs(date).format('HH:mm - D MMM, YYYY');
  }

  getTemporaryInformation(hsaObjects: HsaObject[]): HsaObject[] {
    if (!hsaObjects) {
      return [];
    }

    return hsaObjects.filter((hsa) => {
      if (!hsa.hsaTemporaryInformation) {
        return false;
      }

      const information = hsa.hsaTemporaryInformation;
      const now = dayjs();

      const fromDate = information.fromDate ? dayjs(information.fromDate) : dayjs(now).subtract(1, 'y');
      const toDate = information.toDate ? dayjs(information.toDate) : dayjs(now).add(1, 'y');

      return fromDate.isBefore(now) && now.isBefore(toDate);
    });
  }

  public updateMetaTags(careService: CareService, careproviderUnit: CareproviderUnitItem, routeAddOn: string): string {
    const metaTags: MetaTag[] = [];
    let title = '';
    let description = '';
    let url = '';
    const addOn: string = routeAddOn;

    if (!this.tools.isNil(careService)) {
      title = this.titleBuilder.CreateServiceTitle(
        careService.name,
        careproviderUnit.name,
        careproviderUnit.address.city,
      );
      description = `Läs allt om och boka ${this.titleBuilder.CreateServiceTitle(
        careService.name,
        careproviderUnit.name,
        careproviderUnit.address.city,
      )} på Vården.se`;
      url = this.urlBuilder.CreateStaticLink(
        this.urlBuilder.CreateCareServiceRouterLink(
          careproviderUnit.id,
          careproviderUnit.urlPath,
          careService.id,
          careService.urlPath,
        ),
      );
    } else {
      if (!this.tools.isNil(careproviderUnit.address)) {
        const city: string = careproviderUnit.address.city
          ? careproviderUnit.address.city
          : careproviderUnit.address.municipality;

        const metaTagsTitle: string = careproviderUnit.metaTags.find((tag) => tag.field === MetaTagFieldsEnum.TITLE)
          ?.value;
        const metaTagsDesc: string = careproviderUnit.metaTags.find(
          (tag) => tag.field === MetaTagFieldsEnum.DESCRIPTION,
        )?.value;

        title = metaTagsTitle ? metaTagsTitle : this.titleBuilder.CreateCareproviderTitle(careproviderUnit.name, city);
        const address: string = this.tools.isNil(careproviderUnit.address.visitingAddress)
          ? `${careproviderUnit.address.street} ${careproviderUnit.address.zip} ${city}`
          : careproviderUnit.address.visitingAddress;
        description = metaTagsDesc
          ? metaTagsDesc
          : `Läs allt om och boka tid hos ${title}. Mottagningen ligger på ${address}.`;
      }
      url = this.urlBuilder.CreateStaticLink(
        this.urlBuilder.CreateCareproviderUnitRouterLink(careproviderUnit.id, careproviderUnit.urlPath),
      );
    }

    let followAttValue = 'index';
    if (careService?.noFollow) {
      followAttValue = 'noindex,nofollow';
    }
    if (addOn === RoutesAddonsEnums.CAREPROVIDER_CREATE_REVIEW) {
      followAttValue = 'noindex,nofollow';
    }

    metaTags.push({
      value: followAttValue,
      type: MetaTypesEnum.NAME,
      field: 'robots',
    });

    metaTags.push({
      value: title,
      type: 'title',
      field: 'title',
    });

    metaTags.push({
      value: title,
      type: 'property',
      field: 'og:title',
    });

    metaTags.push({
      value: 'website',
      type: 'property',
      field: 'og:type',
    });

    metaTags.push({
      value: url,
      type: 'property',
      field: 'og:url',
    });

    metaTags.push({
      value: description,
      type: 'name',
      field: 'description',
    });

    metaTags.push({
      value: description,
      type: 'property',
      field: 'og:description',
    });

    metaTags.push({
      value: this.appConfigService.appConfig.siteName,
      type: 'property',
      field: 'og:site_name',
    });

    if (
      !this.tools.isNil(careproviderUnit) &&
      !this.tools.isNil(careproviderUnit.caregiverUnitImages) &&
      !this.tools.isNil(careproviderUnit.caregiverUnitImages.images) &&
      careproviderUnit.caregiverUnitImages.images.length > 0
    ) {
      metaTags.push({
        value: this.tools.getImageUrl(careproviderUnit.caregiverUnitImages.images[0].url),
        type: 'property',
        field: 'og:image',
      });

      metaTags.push({
        value: this.tools.getImageUrl(careproviderUnit.caregiverUnitImages.images[0].url),
        type: 'name',
        field: 'twitter:image',
      });

      metaTags.push({
        value: 'summary_large_image',
        type: 'name',
        field: 'twitter:card',
      });
    }

    this.metaService.updateMetaTags(metaTags);

    this.linkService.removeTag({rel: 'canonical'});
    this.linkService.updateTag({
      rel: 'canonical',
      href: url,
    });

    return title;
  }

  private formatHoursToDisplay(hour: string): string {
    return hour.substring(0, 5);
  }
}
